import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSession } from "next-auth/react";
import qs from "query-string";
import { useRouter } from "next/router";
import { isNumber, omit } from "lodash-es";
import Cookies from "js-cookie";
import {
  localeAdapter,
  productListClickedProps,
  TrackFnReturnType,
  SegmentProductClicked,
  useSegment,
  requestSource,
  GatedPopupRedirectData,
} from "src/hooks/useSegment";
import { googleTagManager } from "src/tracking";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import { guestLogin } from "src/utils/login/guestLogin";
import { Locale } from "src/lib/Intl";
import { useMyOrders } from "./useMyOrders";
import {
  getVisitorTypeFromOrders,
  setLongCookie,
  setShortCookie,
} from "src/utils/prospect-vs-customer";
import { popPostLoginWishlistItem } from "src/utils/postLoginWishlist";
import { useAddToWishList } from "./useAddToWishlist";
import {
  GATED_ADD_PRODUCT_TO_FAVOURITE_SOURCE,
  GATED_ADD_TO_PRODUCT_SOURCE,
  GATED_BRAND_CAROUSEL_VIEW_ALL_SOURCE,
  GATED_BRAND_TO_FAVOURITE_SOURCE,
  GATED_MEET_BRAND_CTA,
  GATED_POPUP_REDIRECT_DATA,
  GATED_POPUP_SOURCE,
  GATED_SEARCH_POPULAR_BRAND,
  GATED_SEARCH_SUGGESTION_ITEM,
} from "src/constants/gatedItem";
import {
  SEGMENT_EVENTS,
  SEGMENT_LIST_FORMAT,
  SEGMENT_PROMOTION_TYPE,
  SegmentProductListMeta,
} from "src/segment";
import {
  CatalogProduct,
  HomepageSingleCategoryProduct,
  SingleProduct,
} from "src/types/graphql.d";
import { getAnalytics } from "src/utils/getAnalytics";
import { addPostLoginAddedProduct } from "src/utils/postLoginCartAdded";
import { useIsLoggedIn } from "./useIsLoggedIn";
import { Session } from "next-auth";
import {
  deleteSessionStorageItem,
  getSessionStorageJSON,
} from "src/utils/helper/sessionStorage";
import { isConfirmationStep } from "src/utils/checkout";
import { OT_CHECKOUT_CUSTOMER } from "src/utils/constants";
import { useSigningOut } from "src/hooks/stores/use-app-store";

export interface SegmentPopupRedirect {
  gatedActionType?: string;
  position?: number;
  product?: CatalogProduct | HomepageSingleCategoryProduct;
  productListMeta?: SegmentProductListMeta;
  promo_id?: string;
  promo_name?: string;
  promo_creative?: string;
  promo_position?: string;
  promo_type?: SEGMENT_PROMOTION_TYPE | string;
  promo_path?: string;
  actionSource?: string;
  tab?: string;
  addToCartData?: {
    variationId: string;
    productId: string;
    product: SingleProduct;
  };
  shopTypeFromRedirect?: string;
  type?: string;
  brandName?: string;
  gateLevel?: string;
}
const setAuthTokenCookie = (token: string) => {
  Cookies.set("ot_auth_token", token, {
    path: "/",
    sameSite: "strict",
  });
};

const setLocale = (locale: string) => {
  Cookies.set("locale", locale, {
    path: "/",
    sameSite: "strict",
  });
};

async function redirect(
  segmentProductClicked: (args: SegmentProductClicked) => TrackFnReturnType,
  segmentProductListClicked: (
    data: productListClickedProps
  ) => TrackFnReturnType,
  segmentProductViewed: (
    product: SingleProduct,
    isLoggedIn?: boolean,
    gatedActionType?: string,
    redirectUrl?: string,
    actionSource?: string
  ) => TrackFnReturnType,
  gatedSearchResultRedirected: (props: {
    actionSource: string;
    gatedActionType?: string;
    brandName?: string;
    shopType?: string;
    gateLevel?: string;
    redirectTo: string;
  }) => TrackFnReturnType,
  gatedPopupRedirected: (data: GatedPopupRedirectData) => TrackFnReturnType,
  isLoggedIn: boolean
): Promise<void> {
  const query = qs.parse(window.location.search);
  const { htevents } = getAnalytics();
  // TODO: Remove once one version of SalesLanding Page is available
  // Add `new Date().getTime() + ""` to invalidate browser cache
  if (query.design) {
    query.time = `${new Date().getTime()}`;
  }

  const redirectUrl =
    (query.redirect_to as string) ||
    qs.stringifyUrl({
      url: window.location.pathname,
      query: omit(query, ["signin"]),
    });

  if (query.source === GATED_POPUP_SOURCE) {
    const redirectData = getSessionStorageJSON<SegmentPopupRedirect>(
      GATED_POPUP_REDIRECT_DATA
    );

    if (redirectData) {
      const {
        gatedActionType,
        position,
        product,
        productListMeta: { listFormat, listId, listType, shopType } = {
          listFormat: SEGMENT_LIST_FORMAT.default,
          listId: "",
          listType: "",
          shopType: "",
        },
        promo_id,
        promo_name,
        promo_creative,
        promo_position,
        promo_type,
        promo_path,
        actionSource,
        addToCartData,
        shopTypeFromRedirect,
        brandName,
        gateLevel,
      } = redirectData;

      if (
        actionSource === GATED_SEARCH_SUGGESTION_ITEM ||
        actionSource === GATED_SEARCH_POPULAR_BRAND
      ) {
        void gatedSearchResultRedirected({
          actionSource,
          gatedActionType,
          brandName,
          shopType: shopTypeFromRedirect,
          gateLevel,
          redirectTo: redirectUrl,
        });
      } else if (actionSource === GATED_MEET_BRAND_CTA) {
        void gatedPopupRedirected({
          promo_type,
          gated_action_type: gatedActionType,
          shop_type: shopTypeFromRedirect,
          redirect_to: redirectUrl,
        });
      } else if (
        promo_position ||
        actionSource === GATED_BRAND_CAROUSEL_VIEW_ALL_SOURCE
      ) {
        await htevents.track(SEGMENT_EVENTS.gatedPopupRedirected, {
          ...requestSource,
          promo_id,
          promo_name,
          promo_creative,
          promo_position,
          promo_type,
          promo_path,
          shop_type: shopType || shopTypeFromRedirect,
          redirect_to: redirectUrl,
          gated_action_type: gatedActionType,
        });
      } else if (product && isNumber(position)) {
        void segmentProductClicked({
          position,
          product,
          productListMeta: {
            listFormat,
            listId,
            listType,
            shopType,
          },
          gatedActionType,
          actionSource,
          redirectTo: redirectUrl,
        });
      } else if (
        (actionSource === GATED_ADD_TO_PRODUCT_SOURCE ||
          actionSource === GATED_BRAND_TO_FAVOURITE_SOURCE ||
          actionSource === GATED_ADD_PRODUCT_TO_FAVOURITE_SOURCE) &&
        addToCartData
      ) {
        const { variationId, productId, product } = addToCartData;
        void segmentProductViewed(
          product,
          isLoggedIn,
          gatedActionType,
          redirectUrl,
          actionSource
        );

        if (actionSource === GATED_ADD_TO_PRODUCT_SOURCE) {
          addPostLoginAddedProduct({ variationId, productId });
        }
      } else {
        void segmentProductListClicked({
          listFormat,
          listId,
          listType,
          shopType,
          gatedActionType,
          actionSource,
        });
      }
    }
  }
}

function useSessionChange(locale: Locale) {
  const {
    segmentIdentify,
    segmentSignedIn,
    segmentSignedOut,
    segmentSignedUp,
    segmentProductClicked,
    segmentProductListClicked,
    segmentProductViewed,
    gatedSearchResultRedirected,
    gatedPopupRedirected,
    user,
  } = useSegment();
  const router = useRouter();
  const { data: session } = useSession();
  const { refetch: fetchMyOrders } = useMyOrders({ limit: 1, skip: true });
  const { addToWishlist } = useAddToWishList({
    refetchQueries: ["userWishlistMetaData"],
  });

  const prevSessionRef = useRef<Session | null>();
  const wasLoggedInRef = useRef(false);
  const isGuestLoginRef = useRef(false);
  const signOutTracked = useRef(false);

  const hasNoSession = useMemo(
    () => prevSessionRef.current === undefined && session === null,
    [session]
  );
  const hasSessionEnded = useMemo(
    () => prevSessionRef.current !== undefined && session === null,
    [session]
  );

  const isLoggedIn = useIsLoggedIn();
  const signingOut = useSigningOut();
  const isSignedInRef = useRef(false);

  useEffect(() => {
    if (locale) {
      setLocale(locale as string);
    }
  }, [locale]);

  useEffect(() => {
    if (prevSessionRef.current?.accessToken === session?.accessToken) {
      return;
    }

    setAuthTokenCookie(session?.accessToken || "");
    prevSessionRef.current = session;
  }, [session]);

  useEffect(() => {
    if (session) {
      wasLoggedInRef.current =
        session.user.signInPlatform !== AuthProvider.GUEST_LOGIN;
    }
  }, [session]);

  useEffect(() => {
    if (
      isGuestLoginRef.current ||
      router.pathname.startsWith("/app-login") ||
      !hasNoSession
    ) {
      return;
    }

    guestLogin()
      .then((res) => {
        isGuestLoginRef.current = true;
      })
      .catch((error) => {
        console.error("Guest login failed", error);
      });
  }, [session, hasNoSession, router.pathname]);

  useEffect(() => {
    if (signOutTracked.current || !hasSessionEnded) {
      return;
    }

    // NOTE: Session ended
    if (user?.id?.()) {
      segmentSignedOut();
      signOutTracked.current = true;
    }

    if (wasLoggedInRef.current) {
      deleteSessionStorageItem(OT_CHECKOUT_CUSTOMER);
    }

    if (wasLoggedInRef.current && signingOut && isConfirmationStep(router)) {
      window.location.replace("/");
    } else if (router.isReady) {
      void router.reload();
    }
  }, [user, router, hasSessionEnded, segmentSignedOut, signingOut]);

  useEffect(() => {
    if (session?.user.user_id && session?.user.email) {
      if (window.NREUM) {
        window.NREUM.setCustomAttribute("enduser.id", session.user.user_id);
      }
    }
  }, [session?.user.user_id, session?.user.email]);

  const handleSessionEvents = useCallback(async () => {
    if (!session) {
      return;
    }

    // NOTE: Tracking: Segment, Google Analytics
    if (session.user?.new_signup === true) {
      await segmentIdentify({
        sign_up_platform: session.user?.signInPlatform,
        name: session.user?.name,
        locale: localeAdapter(locale),
      });

      await segmentSignedUp();
    }

    if (session.user?.new_signup === false) {
      await segmentIdentify({
        sign_in_platform: session.user?.signInPlatform,
        locale: localeAdapter(locale),
      });
      await segmentSignedIn();

      if (session.user.signInPlatform !== AuthProvider.CREDENTIALS_SOFT_LOGIN) {
        try {
          const { data } = await fetchMyOrders();
          const visitorType = getVisitorTypeFromOrders(data);

          setLongCookie(visitorType);
          setShortCookie(visitorType);
        } catch (e) {
          console.error("Cannot get my orders", e);
        }
      }
    }

    if (session.user.signInPlatform !== AuthProvider.CREDENTIALS_SOFT_LOGIN) {
      const item = popPostLoginWishlistItem();
      if (item) {
        await addToWishlist(item.itemType, item.itemId).catch(() => {});
      }
    }

    // NOTE: Session Soft Login started
    if (
      session.user?.signInPlatform === AuthProvider.CREDENTIALS_SOFT_LOGIN &&
      session.user.email
    ) {
      const { email } = session.user;
      googleTagManager.trackUser(undefined, email);
    }

    if (!session.user.opt_in && !session.user.first_order) {
      Cookies.set("client_opt_in", "1");
    }

    await redirect(
      segmentProductClicked,
      segmentProductListClicked,
      segmentProductViewed,
      gatedSearchResultRedirected,
      gatedPopupRedirected,
      isLoggedIn
    );
  }, [
    session,
    locale,
    fetchMyOrders,
    addToWishlist,
    isLoggedIn,
    segmentIdentify,
    segmentSignedIn,
    segmentSignedUp,
    segmentProductClicked,
    segmentProductListClicked,
    segmentProductViewed,
    gatedSearchResultRedirected,
    gatedPopupRedirected,
  ]);

  useEffect(() => {
    // NOTE: Session change in progress (signin=true):
    // - changed from Guest Login to Login
    // - changed from Guest Login to Soft Login
    // - changed from Soft Login to Login
    if (
      !isLoggedIn ||
      !session ||
      isSignedInRef.current ||
      session.user.signInPlatform === AuthProvider.GUEST_LOGIN
    ) {
      return;
    }

    isSignedInRef.current = true;
    void handleSessionEvents();
  }, [session, router, isLoggedIn, handleSessionEvents]);
}

export { useSessionChange };
